// src/components/routes/Header/Header.js

import React from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { handleLogoutApi } from '../../../utils/api';
import './Header.css';

const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [hasInstance, setHasInstance] = React.useState(false);
    const [instanceId, setInstanceId] = React.useState(null);
    const [isWhatsAppConnected, setIsWhatsAppConnected] = React.useState(false);
    
    // Extract instance ID from the current path
    const pathParts = location.pathname.split('/');
    const currentInstanceId = pathParts.length > 1 ? pathParts[1] : null;
    
    const isLoggedIn = !!localStorage.getItem("token");

    React.useEffect(() => {
        const checkInstance = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    setHasInstance(false);
                    setIsWhatsAppConnected(false);
                    return;
                }

                const apiUrl = process.env.REACT_APP_API_URL;
                const response = await fetch(`${apiUrl}/user-instance`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setHasInstance(data.hasInstance);
                    
                    // If we have an instanceId in the path but no instance set, update it
                    if (data.hasInstance && currentInstanceId) {
                        setInstanceId(currentInstanceId);
                    } else if (data.hasInstance && data.instanceId) {
                        setInstanceId(data.instanceId);
                    }
                    
                    // Set WhatsApp connection status from the response if available
                    setIsWhatsAppConnected(data.isConnected || false);
                    
                    // If we have an instance, check the connection status directly
                    // This ensures we have the most up-to-date connection status
                    const idToCheck = currentInstanceId || data.instanceId;
                    if (data.hasInstance && idToCheck) {
                        await checkConnectionStatus(idToCheck);
                    }
                }
            } catch (error) {
                console.error('Error checking instance:', error);
                setHasInstance(false);
                setIsWhatsAppConnected(false);
            }
        };
        
        const checkConnectionStatus = async (instanceId) => {
            try {
                const token = localStorage.getItem('token');
                if (!token || !instanceId) return;
                
                const apiUrl = process.env.REACT_APP_API_URL;
                const response = await fetch(`${apiUrl}/${instanceId}/status`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                
                if (response.ok) {
                    const data = await response.json();
                    // Handle different response formats from the backend
                    const isConnected = 
                        data.connected || // Direct connected flag
                        data.status === 'connected' || // Status string
                        (data.success && data.isConnected); // Success with isConnected flag
                    
                    // Check if connection status has changed
                    const previousConnectionStatus = isWhatsAppConnected;
                    setIsWhatsAppConnected(isConnected);
                    
                    // If connection status has changed, update localStorage
                    // but don't trigger reloads from the Header component
                    if (isConnected && !previousConnectionStatus) {
                        localStorage.setItem('whatsapp_connected', 'true');
                        // Don't reload from here - the QR code component handles that
                    } else if (!isConnected && previousConnectionStatus) {
                        localStorage.removeItem('whatsapp_connected');
                    }
                }
            } catch (error) {
                console.error('Error checking WhatsApp connection:', error);
                setIsWhatsAppConnected(false);
            }
        };

        checkInstance();
        
        // If we have a current instance ID, also check its connection status
        if (currentInstanceId) {
            checkConnectionStatus(currentInstanceId);
            
            // Set up polling to periodically check connection status
            // Use a longer interval to reduce unnecessary API calls
            const statusInterval = setInterval(() => {
                checkConnectionStatus(currentInstanceId);
            }, 60000); // Check every minute
            
            // Clean up interval on component unmount
            return () => clearInterval(statusInterval);
        }
    }, [isLoggedIn, currentInstanceId]); // Re-run when login status or instanceId changes

    const handleLogout = async () => {
        const confirmLogout = window.confirm("Are you sure you want to log out?");
        if (!confirmLogout) return;
        try {
            await handleLogoutApi(); // This will handle the device ID  and redirect
        } catch (error) {
            console.error('Error during logout:', error);
            // The logout function will handle cleanup and redirect even on error
        }
    };

    const handleLogoClick = () => {
        // If user is not logged in, go to login
        if (!isLoggedIn) {
            navigate('/login');
            return;
        }
        
        // If user has instance and WhatsApp is connected, go to message page
        if (hasInstance && instanceId && isWhatsAppConnected) {
            navigate(`/${instanceId}/message`);
        } 
        // If user has instance but WhatsApp is not connected, go to QR code page
        else if (hasInstance && instanceId) {
            navigate(`/${instanceId}/qrcode`);
        } 
        // Otherwise, go to create instance page
        else {
            navigate('/create-instance');
        }
    };

    return (
        <div className="header-container">
            <img
                src="/uploads/logo.webp"
                className="logo"
                alt="Company Logo"
                aria-label="Logo"
                onClick={handleLogoClick}
                style={{ cursor: 'pointer' }}
            />
            <div className="header-right">
                {isLoggedIn && (
                    <>
                    {/* Only show Reports and Messenger buttons if WhatsApp is connected */}
                    {hasInstance && currentInstanceId && isWhatsAppConnected && (
                        <>
                        <Link 
                            to={`/${currentInstanceId}/reports`}
                            className="reports-link"
                            title="View Reports"
                        >
                            <button className="reports-button">
                                <i className="fas fa-chart-bar"></i>
                                Reports
                            </button>
                        </Link>
                        <Link 
                            to={`/${currentInstanceId}/message`}
                            className="reports-link"
                            title="View Messenger"
                        >
                            <button className="reports-button">
                        <img 
                            src="/uploads/chat-text.png"
                            className="proo-icon"
                            alt="chat-text icon"
                            aria-label="chat-text"
                        />
                                Messenger
                            </button>
                        </Link>
                        </>
                    )}

                    <div className="profile-section">
                        <img 
                            src="/uploads/profile.svg"
                            className="pro-icon"
                            alt="profile icon"
                            aria-label="Profile"
                            onClick={handleLogout}
                        />
                        <span 
                            onClick={handleLogout} 
                            style={{ 
                                cursor: 'pointer', 
                                marginLeft: '8px', 
                                fontSize: '0.85rem', 
                                color: '#666' 
                            }}
                        >
                            Logout
                        </span>
            </div>
                    </>
                )}
                {!isLoggedIn && (
                    <Link to="/login">
                        <img 
                            src="/uploads/profile.svg"
                            className="pro-icon"
                            alt="profile icon"
                            aria-label="Profile"
                        />
                    </Link>
                )}
            </div> 
        </div>
    );
};

export default Header;
